import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { AlignmentGrade, PostureCheck } from '@erectus/shared';
import { Card } from '@/ui/primitives.js';
import { useWarm } from '@/lib/warmCache.js';
import { recentPostureChecks } from '@/lib/repo.js';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Worst grade across the standing measures — the one word the card shows. */
function overall(check: PostureCheck): AlignmentGrade | null {
  const grades = Object.values(check.standing?.grades ?? {});
  if (grades.length === 0) return null;
  if (grades.includes('forward')) return 'forward';
  if (grades.includes('leaningBack')) return 'leaningBack';
  if (grades.includes('slightlyForward')) return 'slightlyForward';
  return 'aligned';
}

const GRADE_WORD: Record<AlignmentGrade, string> = {
  aligned: 'aligned',
  slightlyForward: 'a little forward',
  forward: 'forward',
  leaningBack: 'leaning back',
};

const day = (at: number): string =>
  new Date(at).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });

/** Progress card for the two posture checks: the last of each, and a nudge after a week. */
export function PostureCheckCard({ uid }: { uid: string }): ReactNode {
  const [state] = useWarm<Array<PostureCheck & { id: string }>>(
    `checks:${uid}`,
    () => recentPostureChecks(uid),
    'Could not load your posture checks.',
  );
  const checks = state.status === 'ready' ? state.data : [];
  const seated = checks.find((c) => c.kind === 'seated' || c.seated);
  const standing = checks.find((c) => c.kind === 'standing');
  const latest = Math.max(seated?.at ?? 0, standing?.at ?? 0);
  const due = latest === 0 || Date.now() - latest > WEEK_MS;
  const grade = standing ? overall(standing) : null;

  return (
    <Card className="flex items-center justify-between gap-3">
      <div className="min-w-0 space-y-0.5">
        <p className="font-semibold">Posture check</p>
        <p className="text-xs text-muted">
          Sitting baseline:{' '}
          {seated?.seated?.cva != null ? `${seated.seated.cva.toFixed(0)}° · ${day(seated.at)}` : 'not yet'}
        </p>
        <p className="text-xs text-muted">
          Standing: {standing && grade ? `${GRADE_WORD[grade]} · ${day(standing.at)}` : 'not yet'}
        </p>
      </div>
      <Link
        to="/check"
        className={`shrink-0 rounded-xl px-4 py-2.5 text-sm font-semibold ${due ? 'bg-ink text-white' : 'border border-line text-ink'}`}
      >
        {latest === 0 ? 'Start' : due ? 'Check again' : 'Open'}
      </Link>
    </Card>
  );
}
